<template>
  <section class="landing-card gap-4">
    <!-- Social Proof Badge (Top) -->
    <div class="social-proof-badge">
      <span class="badge-icon">✓</span>
      <span class="badge-text">{{ formattedGiftCardsCount }}+ gifts created</span>
    </div>
    <div class="cards-carousel-compact">
      <div class="cards-scroll-track">
        <GiftCardPreview
          v-for="(card, index) in displayCards"
          :key="`card-${index}`"
          :balance="card.balance"
          :currency="card.currency"
          :bg-style="card.bgStyle"
          :message="card.message"
        />
      </div>
    </div>

    <!-- Text content group -->
    <div class="flex flex-col items-start gap-3 self-stretch">
      <!-- Main headline -->
      <h2 class="hero-title mt-3">
        Send Crypto as a Gift
      </h2>

      <!-- Subheadline -->
      <p class="hero-subtitle">
        No Borders & in a Non-Custodial Way
      </p>
    </div>

    <!-- Description and CTA group -->
    <div class="flex flex-col items-start gap-3 self-stretch">
      <!-- Description -->
      <p class="hero-description">
        Create a crypto gift, add a message, and share the link – your friend redeems it to their wallet in seconds
      </p>

      <!-- CTA Button -->
      <button
        @click="navigateToCreate"
        class="hero-cta-button"
      >
        Send a crypto gift
      </button>
    </div>
    
  </section>
</template>

<script setup lang="ts">
import { inject, computed, ref, onMounted } from 'vue'
import GiftCardPreview from '../ui/GiftCardPreview.vue'

const openGiftBoxModal = inject<() => void>('openGiftBoxModal')

const navigateToCreate = () => {
  if (openGiftBoxModal) {
    openGiftBoxModal()
  }
}

// Social proof data
const totalGiftCards = ref(1247) // This could be fetched from API

const formattedGiftCardsCount = computed(() => {
  return totalGiftCards.value.toLocaleString('en-US')
})

// Sample messages for random cards
const sampleMessages = [
  'Happy Birthday!',
  'Congratulations!',
  'For my best friend',
  'Enjoy your gift',
  'Thanks for everything',
  'Good luck with new job',
  'Well done!',
  'Welcome to the team',
  'Merry Christmas',
  'Happy Anniversary',
  'To my favorite person',
  'You deserve this',
  'Just because',
  'Thinking of you',
  'Best wishes',
  'Happy Graduation',
  'Welcome home',
  'Get well soon',
  'Safe travels',
  'Happy Holidays',
  'Congrats on new house',
  'For your special day',
  'With love from Mom',
  'From secret admirer'
]

// Generate random gift cards for display
const generateRandomCard = (id: number) => {
  const balances = ['0.0025', '0.0050', '0.0100', '0.0250', '50.00', '100.00', '250.00', '500.00']
  const currencies = ['BTC', 'USDC']

  return {
    id,
    balance: balances[Math.floor(Math.random() * balances.length)],
    currency: currencies[Math.floor(Math.random() * currencies.length)],
    bgStyle: Math.floor(Math.random() * 5), // 0-4 for 5 available styles
    message: sampleMessages[Math.floor(Math.random() * sampleMessages.length)]
  }
}

// Create a long list of cards for infinite scroll effect
const displayCards = ref<any[]>([])

onMounted(() => {
  // Generate 10 random cards
  const baseCards = Array.from({ length: 10 }, (_, i) => generateRandomCard(i))
  // Duplicate cards for seamless infinite scroll
  displayCards.value = [...baseCards, ...baseCards]
})
</script>

<style scoped>
.hero-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  letter-spacing: 0.01em;
  color: #182855;
  align-self: stretch;
}

.hero-subtitle {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 22px;
  letter-spacing: 0.01em;
  color: #182855;
  align-self: stretch;
}

.hero-description {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #182855;
  align-self: stretch;
}

.hero-cta-button {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 80px;
  gap: 10px;
  height: 48px;
  background: #162550;
  border-radius: 12px;
  align-self: stretch;

  /* Text */
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.02em;
  color: #FFFFFF;

  /* Interaction */
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hero-cta-button:hover {
  background: #1c2f66;
  transform: translateY(-1px);
}

.hero-cta-button:active {
  transform: translateY(0);
}

/* Social Proof Badge (Top) */
.social-proof-badge {
  @apply inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full self-start;
  background: rgba(22, 37, 80, 0.08);
  border: 1px solid rgba(22, 37, 80, 0.12);
}

.badge-icon {
  @apply text-[#162550];
  font-size: 10px;
  font-weight: 700;
}

.badge-text {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 11px;
  line-height: 13px;
  color: #162550;
  opacity: 0.8;
}

/* Compact Cards Carousel */
.cards-carousel-compact {
  @apply relative overflow-hidden;
  height: 90px;
  width: 100%;
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
}

.cards-scroll-track {
  @apply flex gap-2.5 items-center;
  animation: scroll-cards 20s linear infinite;
  width: max-content;
  padding-left: 16px;
}

.cards-scroll-track:hover {
  animation-play-state: paused;
}

@keyframes scroll-cards {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>
